/**
 * HotelCombobox
 *
 * Compact click-to-open hotel picker. Default state is a slim button showing
 * the current selection (or a "Otel seçin..." placeholder). Clicking the
 * button expands an inline dropdown with:
 *   - search input
 *   - admin-curated chip list (2-column grid)
 *   - "Başka Otel Yaz" CTA → switches to free-text mode
 *
 * Iter 31: user asked to "open the list on click" rather than always show
 * it — the always-open version pushed the rest of the wizard too far down.
 * Click-outside dismisses; Escape key closes; selecting a hotel closes.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import panelApi from "@/lib/panelApi";
import { Input } from "@/components/ui/input";
import { Hotel, Plus, Search, Check, Pencil, ArrowLeft, ChevronDown, X } from "lucide-react";

export default function HotelCombobox({ value, onChange, disabled = false }) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [query, setQuery] = useState("");
  // "list" = chip grid + search; "custom" = free-text input inside the panel.
  const [mode, setMode] = useState("list");
  const [customName, setCustomName] = useState(value || "");
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  // Load once; the list is small enough (~50 rows) to keep client-side.
  useEffect(() => {
    let alive = true;
    panelApi.get("/hotels").then((r) => {
      if (alive) setItems(Array.isArray(r.data) ? r.data : []);
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  // If a value comes from outside (edit/AI prefill) and is not in the
  // curated list, jump straight into "custom" mode so the agency sees
  // their typed name instead of an empty search box when they expand.
  useEffect(() => {
    if (!value) return;
    const inList = items.some((h) => (h.name || "").toLowerCase() === value.toLowerCase());
    if (!inList) {
      setMode("custom");
      setCustomName(value);
    }
  }, [value, items]);

  // Click-outside + Escape to close the dropdown.
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((h) => (h.name || "").toLowerCase().includes(q));
  }, [items, query]);

  const pickChip = (name) => {
    onChange?.(name);
    setCustomName(name);
    setMode("list");
    setOpen(false); // Selection closes the dropdown — snappy UX.
  };

  const commitCustom = async () => {
    const n = customName.trim();
    if (!n) return;
    onChange?.(n);
    if (busy) return;
    if (items.some((h) => (h.name || "").toLowerCase() === n.toLowerCase())) return;
    setBusy(true);
    try {
      const r = await panelApi.post("/hotels", { name: n });
      const added = r.data;
      setItems((prev) => {
        if (prev.some((h) => (h.name || "").toLowerCase() === (added.name || "").toLowerCase())) return prev;
        return [added, ...prev];
      });
    } catch {} finally {
      setBusy(false);
    }
  };

  const clearSelection = (e) => {
    e.stopPropagation();
    onChange?.("");
    setCustomName("");
    setQuery("");
    setMode("list");
  };

  return (
    <div ref={rootRef} className="relative" data-testid="hotel-combobox">
      {/* Trigger — shows current selection; opens the panel on click. */}
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        data-testid="hotel-combobox-trigger"
        className={`w-full flex items-center gap-2 rounded-md border border-input bg-white px-3 py-2 h-10 text-sm transition-colors ${
          disabled ? "opacity-60" : "hover:border-brand-orange/60"
        } ${open ? "border-brand-orange ring-2 ring-brand-orange/20" : ""}`}
      >
        <Hotel size={14} className={value ? "text-brand-orange" : "text-slate-400"} />
        <span className={`flex-1 text-left truncate ${value ? "text-brand-navy" : "text-slate-400"}`}>
          {value || "Otel seçin veya listeyi açmak için tıklayın"}
        </span>
        {value && !disabled && (
          <span
            role="button"
            tabIndex={0}
            onClick={clearSelection}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") clearSelection(e); }}
            className="text-slate-400 hover:text-rose-600 p-1 -m-1 rounded"
            data-testid="hotel-combobox-clear"
          >
            <X size={14} />
          </span>
        )}
        <ChevronDown size={14} className={`text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {/* Dropdown panel — anchored below the trigger. */}
      {open && (
        <div
          className="absolute top-full left-0 right-0 mt-1 z-30 rounded-xl border border-slate-200 bg-white shadow-xl p-3 grid gap-3"
          data-testid="hotel-combobox-panel"
        >
          {mode === "custom" ? (
            <>
              <div className="relative">
                <Pencil size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                <Input
                  value={customName}
                  onChange={(e) => {
                    setCustomName(e.target.value);
                    onChange?.(e.target.value);
                  }}
                  onBlur={commitCustom}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      commitCustom();
                      setOpen(false);
                    }
                  }}
                  placeholder="Otel adını yazın (örn. Swissôtel Al Maqam)"
                  className="pl-9"
                  autoFocus
                  data-testid="hotel-combobox-input"
                  autoComplete="off"
                />
              </div>
              <div className="flex items-center justify-between text-[11px] text-slate-500">
                <span>Yazdığınız otel kaydedince listeye otomatik eklenir.</span>
                <button
                  type="button"
                  onClick={() => setMode("list")}
                  className="inline-flex items-center gap-1 text-brand-orange hover:text-brand-orange-dark font-semibold"
                  data-testid="hotel-combobox-back-list"
                >
                  <ArrowLeft size={12} /> Listeden Seç
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                <Input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Listedeki otelleri ara..."
                  className="pl-9"
                  data-testid="hotel-combobox-search"
                  autoComplete="off"
                  autoFocus
                />
              </div>
              <div
                className="max-h-64 overflow-y-auto rounded-xl border border-slate-200 bg-white p-2 grid gap-2 sm:grid-cols-2"
                data-testid="hotel-combobox-list"
              >
                {filtered.length === 0 && (
                  <div className="sm:col-span-2 p-4 text-center text-xs text-slate-500">
                    {query ? `"${query}" ile eşleşen otel yok.` : "Henüz otel eklenmedi."}
                  </div>
                )}
                {filtered.map((h) => {
                  const selected = (value || "").toLowerCase() === (h.name || "").toLowerCase();
                  return (
                    <button
                      key={h.id || h.name}
                      type="button"
                      onClick={() => pickChip(h.name)}
                      className={`text-left rounded-xl border-2 px-3 py-2.5 transition-all flex items-center gap-2 ${
                        selected
                          ? "border-brand-orange bg-brand-orange/10 text-brand-navy shadow-sm"
                          : "border-slate-200 bg-canvas-cream/40 hover:border-brand-orange/60 hover:bg-brand-orange/5"
                      }`}
                      data-testid={`hotel-option-${(h.name || "").replace(/\s+/g, "-").toLowerCase()}`}
                    >
                      <Hotel size={14} className={selected ? "text-brand-orange flex-shrink-0" : "text-slate-400 flex-shrink-0"} />
                      <span className="flex-1 min-w-0 text-sm truncate">{h.name}</span>
                      {selected && <Check size={14} className="text-brand-orange flex-shrink-0" />}
                    </button>
                  );
                })}
              </div>
              <button
                type="button"
                onClick={() => setMode("custom")}
                className="w-full rounded-xl border-2 border-dashed border-brand-orange/40 bg-brand-orange/5 hover:bg-brand-orange/10 hover:border-brand-orange px-4 py-3 flex items-center justify-center gap-2 text-sm text-brand-orange-dark font-semibold transition-colors"
                data-testid="hotel-combobox-switch-custom"
              >
                <Plus size={14} /> Listede yok mu? Başka Otel Yaz
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
